/**
 * MediaPicker Component for Support MiniApp
 * Provides modal UI for selecting media via DG SuperApp bridge
 */

import { useState } from 'react';
import { Camera, FileText, Image as ImageIcon, Paperclip, X } from 'lucide-react';
import { useBridge } from '../../hooks/useBridge';
import type { Attachment } from '../../types';

interface MediaPickerProps {
  onSelect: (attachment: Attachment) => void;
  onClose: () => void;
}

export function MediaPicker({ onSelect, onClose }: MediaPickerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { capturePhoto, pickPhoto, selectFile } = useBridge();

  const handleCapturePhoto = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const attachment = await capturePhoto();
      if (attachment) {
        onSelect(attachment);
      } else {
        setError('Failed to capture photo');
      }
    } catch (err) {
      setError('Camera not available');
      console.error('Camera capture failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePickPhoto = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const attachment = await pickPhoto();
      if (attachment) {
        onSelect(attachment);
      } else {
        setError('No photo selected');
      }
    } catch (err) {
      setError('Failed to pick photo');
      console.error('Photo pick failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectFile = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const attachment = await selectFile();
      if (attachment) {
        onSelect(attachment);
      } else {
        setError('No file selected');
      }
    } catch (err) {
      setError('Failed to select file');
      console.error('File select failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50">
      <div className="bg-white w-full rounded-t-2xl safe-area-bottom animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-700">
              <Paperclip className="h-4 w-4" />
            </span>
            Add Attachment
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-4 mt-4 bg-red-50 text-red-600 text-sm p-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Options */}
        <div className="p-4 space-y-3">
          <MediaOption
            icon={<Camera className="h-5 w-5" />}
            label="Take Photo"
            description="Use camera to capture a photo"
            onClick={handleCapturePhoto}
            disabled={isLoading}
          />

          <MediaOption
            icon={<ImageIcon className="h-5 w-5" />}
            label="Choose from Gallery"
            description="Select a photo from your gallery"
            onClick={handlePickPhoto}
            disabled={isLoading}
          />

          <MediaOption
            icon={<FileText className="h-5 w-5" />}
            label="Choose File"
            description="Select a document or file"
            onClick={handleSelectFile}
            disabled={isLoading}
          />
        </div>

        {/* Cancel Button */}
        <div className="p-4 pt-0">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="w-full py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          >
            Cancel
          </button>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-t-2xl">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Processing...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface MediaOptionProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  disabled: boolean;
}

function MediaOption({ icon, label, description, onClick, disabled }: MediaOptionProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-4 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed touch-active"
    >
      <span className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white text-gray-700 shadow-sm ring-1 ring-gray-200">
        {icon}
      </span>
      <div className="text-left">
        <div className="font-medium text-gray-900">{label}</div>
        <div className="text-sm text-gray-500">{description}</div>
      </div>
    </button>
  );
}
